import React, { FC, useState, useEffect, useMemo } from 'react';
import { Product, Variant } from 'shared/types/product';
import CheckIcon from 'public/icons/ag-icons/check.svg';
import { usePathname, useRouter } from 'next/navigation';
import { useFormat } from 'helpers/hooks/useFormat';

interface MyCustomfitOptionsProps {
  product: Product;
  variant: Variant | undefined;
  scrollable?: boolean;
  setIsCartDisable?: (isCartDisable: boolean) => void;
  onChangeVariant: (updatedSku: string) => void;
  changeRoute: (updatedSku: string) => void;
  showSizeGuide?: React.MouseEventHandler<HTMLSpanElement>;
}

const CustomfitOptions: FC<MyCustomfitOptionsProps> = ({
  product,
  variant,
  scrollable = true,
  setIsCartDisable,
  onChangeVariant,
  changeRoute,
  showSizeGuide,
}) => {
  const router = useRouter();
  const fallbackVariant = variant || undefined;
  const optionsOrder = useMemo(() => {
    return product?.variants[0]?.attributes?.optionsOrder || [];
  }, [product]);
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });

  const [availableOptions, setAvailableOptions] = useState<{ [key: string]: string[] }>({});
  const initialSelectedOptions = useMemo(() => {
    if (!fallbackVariant) return {};
    const result: { [key: string]: string } = {};
    optionsOrder?.forEach((order) => {
      if (fallbackVariant?.attributes?.[order]) {
        result[order] = fallbackVariant.attributes[order];
      }
    });
    return result;
  }, [fallbackVariant, optionsOrder]);
  const [selectedOptions, setSelectedOptions] = useState<{ [key: string]: string }>(initialSelectedOptions);
  // Initialize selected options and available options
  useEffect(() => {
    const newAvailableOptions: { [key: string]: string[] } = {};
    let filteredVariants = product ? [...(product?.variants ?? [])] : [];

    const newSelectedOptions: { [key: string]: string } = { ...selectedOptions };

    optionsOrder?.forEach((order, index) => {
      // Filter variants based on previous selections
      if (index > 0) {
        filteredVariants =
          product?.variants?.filter((v) =>
            optionsOrder
              ?.slice(0, index)
              ?.every(
                (prevOrder) =>
                  !newSelectedOptions?.[prevOrder] || v?.attributes?.[prevOrder] === newSelectedOptions?.[prevOrder],
              ),
          ) || [];
      }

      // Get available options for the current attribute
      const available = Array.from(new Set(filteredVariants?.map((v) => v?.attributes?.[order])?.filter(Boolean)));
      newAvailableOptions[order] = available;

      // Auto-select if there's only one available option AND it's not selected already
      if (available.length === 1 && !newSelectedOptions?.[order]) {
        newSelectedOptions[order] = available?.[0];

        // Further refine filteredVariants based on the auto-selection
        filteredVariants = filteredVariants?.filter((v) => v?.attributes?.[order] === available?.[0]);
      }
    });

    setAvailableOptions(newAvailableOptions);
    // Only update selected options if changed
    if (JSON.stringify(selectedOptions) !== JSON.stringify(newSelectedOptions)) {
      setSelectedOptions(newSelectedOptions);
    }
  }, [product?.variants, selectedOptions]);
  useEffect(() => {
    setSelectedOptions(initialSelectedOptions);
  }, [initialSelectedOptions]);
  // Handle variant selection and SKU update
  useEffect(() => {
    // Only proceed when all options have selections
    const allFieldsSelected = optionsOrder?.every((order) => selectedOptions?.[order]);

    if (allFieldsSelected) {
      const matchedVariant = product?.variants?.find((v) =>
        optionsOrder?.every((order) => v?.attributes?.[order] === selectedOptions?.[order]),
      );
      if (matchedVariant) {
        onChangeVariant(matchedVariant?.sku);
        changeRoute(matchedVariant?.sku);
      }
    }

    if (setIsCartDisable) {
      setIsCartDisable(!allFieldsSelected);
    }
  }, [selectedOptions, product.variants, optionsOrder]);

  const handleSelectChange = (order: string, value: string) => {
    setSelectedOptions((prev) => {
      const newSelected: { [key: string]: string } = { ...prev, [order]: value };

      // Reset all subsequent options
      const resetOrders = optionsOrder?.slice(optionsOrder?.indexOf(order) + 1);
      resetOrders?.forEach((nextOrder) => {
        delete newSelected?.[nextOrder];
      });

      // Auto-select subsequent options if only one option is available
      let filteredVariants = product ? [...(product?.variants ?? [])] : [];
      for (const key of optionsOrder) {
        if (!newSelected?.[key]) break;

        filteredVariants = filteredVariants?.filter((v) => (v?.attributes as any)?.[key] === newSelected?.[key]);
      }

      for (const nextOrder of resetOrders) {
        const availableNext = Array.from(
          new Set(filteredVariants?.map((v) => v?.attributes?.[nextOrder])?.filter(Boolean)),
        );

        if (availableNext?.length === 1) {
          newSelected[nextOrder] = availableNext?.[0];

          // Refine filteredVariants again for this auto-selection
          filteredVariants = filteredVariants?.filter(
            (v) => (v?.attributes as any)?.[nextOrder] === availableNext?.[0],
          );
        } else {
          break;
        }
      }

      return newSelected;
    });
  };

  const orderLabels: { [key: string]: string } = {
    dexterity: 'Choose Dexterity:',
    shafttype: 'Choose Shaft Type',
    flex: 'Choose Flex',
    length: 'Choose Length',
    lieangle: 'Choose Lie Angle',
    loftadjustment: 'Choose Loft Adjustment',
    grip: 'Choose Grip',
    gripthickness: 'Choose Grip Thickness',
    bounce: 'Choose Bounce',
    loft: 'Choose Loft',
    hand: 'Choose Hand',
    colour: 'Choose Colour',
    model: 'Choose Model',
    shaftlength: 'Choose Shaft Length',
    setoptions: 'Choose Set Options',
    shaftlen: 'Shaft Length',
  };

  const optionLables: { [key: string]: string } = {
    dexterity: 'Dexterity:',
    shafttype: 'Shaft Type',
    flex: 'Flex',
    length: 'Length',
    shaftlen: 'Shaft Length',
    setoptions: 'Set Options',
    lieangle: 'Lie Angle',
    loftadjustment: 'Loft Adjustment',
    grip: 'Grip',
    gripthickness: 'Grip Thickness',
    bounce: 'Bounce',
    loft: 'Loft',
    hand: 'Hand',
    colour: 'Colour',
    model: 'Model',
    shaftlength: 'Shaft Length',
  };
  return (
    <div>
      {optionsOrder?.map((order, index) => {
        const uniqueOptions = availableOptions?.[order] || [];
        const isDisabled = index > 0 && !selectedOptions?.[optionsOrder?.[index - 1]];

        return (
          <div key={`${index}-${order}`} className={`relative mt-4 rounded-md p-6`}>
            <div className="flex w-full items-center justify-between pb-4 pr-32">
              <p className="text-14 font-normal">
                {orderLabels?.[order] || order?.charAt(0)?.toUpperCase() + order?.slice(1)}
              </p>
              {index === 0 &&
                product?.sizeGuide !== null &&
                product.sizeGuide &&
                product?.sizeGuide?.tableSet?.length > 0 && (
                  <p className="text-right">
                    <span
                      className="link-arrow dialoglink cursor-pointer text-12 font-normal capitalize underline sm:text-14"
                      onClick={showSizeGuide}
                    >
                      <i>{formatProductMessage({ id: 'clubsSizeGuide', defaultMessage: 'Find Your Fit' })}</i>
                    </span>
                  </p>
                )}
            </div>
            <div className="flex w-[100%] items-center gap-x-12">
              <select
                className={`h-40 w-[100%] gap-y-4 rounded-md py-3 pl-14 text-start text-14 ${
                  selectedOptions?.[order] ? 'appearance-none border-none bg-[#f5f4f3]' : ''
                }`}
                value={selectedOptions?.[order] || ''}
                onChange={(e) => handleSelectChange(order, e.target.value)}
                // disabled={isDisabled}
                style={{
                  appearance: 'none',
                  backgroundImage:
                    "url(\"data:image/svg+xml;charset=UTF-8,%3Csvg width='14' height='9' viewBox='0 0 14 9' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M13 1L7 7L1 1' stroke='%231E1E1E' stroke-width='2'/%3E%3C/svg%3E\")",
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center',
                  backgroundSize: '12px',
                }}
              >
                <option value="" disabled>
                  Select {optionLables?.[order]}
                </option>
                {uniqueOptions?.map((attrValue, valueIdx) => (
                  <option key={`${order}-${valueIdx}`} value={attrValue}>
                    {attrValue?.charAt(0)?.toUpperCase() + attrValue?.slice(1)}
                  </option>
                ))}
              </select>
              {selectedOptions?.[order] ? (
                <div className="right:[-30px] flex h-24 w-24 items-center justify-center rounded-full bg-[#69e8ac] p-2 text-10 font-extrabold text-black sm:right-[-40px]">
                  <CheckIcon />
                </div>
              ) : (
                <div className="right:[-30px] h-24 w-24 rounded-full border border-[#E7E7E7] sm:right-[-40px]"></div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CustomfitOptions;
